import {ResourceDisplayKind} from "crqlar-react-common/dist/models/aggregation/aggregation.model";
import type {FilterRule} from "crqlar-react-common/dist/models/audience/filter.model";
import type {PredefinedValue} from "crqlar-react-common/dist/models/audience/filterOption.model";
import type {Resource} from "crqlar-react-common/dist/models/resource/resource.model";
import {SectorType} from "crqlar-react-common/dist/models/sector/sector.model";
import type {Service} from "crqlar-react-common/dist/models/service/service.model";
import {useAllResourceCategoriesForSector} from "crqlar-react-common/dist/utils/hooks/resources/useAllResourceCategories";
import {
    useAllChildResources,
    useAllRootResources,
} from "crqlar-react-common/dist/utils/hooks/resources/useAllRootResources";
import {useAllSectors} from "crqlar-react-common/dist/utils/hooks/sectors/useSectors";
import {useServicesForSector} from "crqlar-react-common/dist/utils/hooks/services/useServices";
import {getLocalizedValue} from "crqlar-react-common/dist/utils/translations.util";
import {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {getResourceDisplayKind} from "../../common/uiElements/utils";
import DailyListFilterButton from "./DailyListFilterButton";
import {
    createCrmDailyListFilterOptions,
    createRestaurantDailyListFilterOptions,
    createSpaDailyListFilterOptions,
    predefinedValue,
} from "./dailyListFilterOptions";

interface ControlledDailyListFilterButtonProps {
    rules: FilterRule[];
    onChange: (rules: FilterRule[]) => void;
}

interface SpaDailyListFilterButtonProps extends ControlledDailyListFilterButtonProps {
    sectorId: string;
    resources: readonly Resource[];
}

interface RestaurantDailyListFilterButtonProps extends ControlledDailyListFilterButtonProps {
    resources: readonly Resource[];
}

const sortValues = (values: PredefinedValue[], locale: string) => {
    const collator = new Intl.Collator(locale, {numeric: true, sensitivity: "base"});
    return values.sort((left, right) => collator.compare(left.name, right.name));
};

const resourceName = (resource: Resource, locale: string, fallback: string) => (
    getLocalizedValue(locale, resource.nameLocalized) || resource.name || fallback
);

const useAccommodationRoomValues = (locale: string, unnamedLabel: string) => {
    const sectors = useAllSectors();
    const rootResources = useAllRootResources();

    return useMemo(() => {
        const accommodationSectorIds = new Set(
            sectors
                .filter(sector => sector.sectorType === SectorType.ACCOMMODATION)
                .map(sector => sector.id)
                .filter((id): id is string => Boolean(id)),
        );

        return sortValues(
            rootResources
                .filter(resource => (
                    resource.id
                    && resource.sectorIds?.some(id => accommodationSectorIds.has(id))
                ))
                .map(resource => predefinedValue(
                    resource.id!,
                    resourceName(resource, locale, unnamedLabel),
                    resource.meta?.tagColor,
                )),
            locale,
        );
    }, [locale, rootResources, sectors, unnamedLabel]);
};

export function CrmDailyListFilterButton({
    rules,
    onChange,
}: ControlledDailyListFilterButtonProps) {
    const {t, i18n} = useTranslation();
    const locale = i18n.resolvedLanguage || i18n.language;
    const rooms = useAccommodationRoomValues(locale, t("unnamed"));
    const filterOptions = useMemo(
        () => createCrmDailyListFilterOptions({t, locale, rooms}),
        [locale, rooms, t],
    );

    return <DailyListFilterButton filterOptions={filterOptions} rules={rules} onChange={onChange}/>;
}

export function SpaDailyListFilterButton({
    sectorId,
    resources,
    rules,
    onChange,
}: SpaDailyListFilterButtonProps) {
    const {t, i18n} = useTranslation();
    const locale = i18n.resolvedLanguage || i18n.language;
    const unnamedLabel = t("unnamed");
    const rooms = useAccommodationRoomValues(locale, unnamedLabel);
    const sectors = useAllSectors();
    const categories = useAllResourceCategoriesForSector(sectorId);
    const services = useServicesForSector(sectorId);

    const spaValues = useMemo(() => {
        const sectorsById = new Map(sectors.flatMap(sector => (
            sector.id ? [[sector.id, sector] as const] : []
        )));
        const categoriesById = new Map(categories.flatMap(category => (
            category.id ? [[category.id, category] as const] : []
        )));
        const staff: PredefinedValue[] = [];
        const facilities: PredefinedValue[] = [];

        resources.forEach(resource => {
            if (!resource.id) return;

            const value = predefinedValue(
                resource.id,
                resourceName(resource, locale, unnamedLabel),
                resource.meta?.tagColor,
            );
            const kind = getResourceDisplayKind(resource, sectorsById, categoriesById);
            if (kind === ResourceDisplayKind.FACILITY) facilities.push(value);
            if (kind === ResourceDisplayKind.STAFF) staff.push(value);
        });

        return {
            staff: sortValues(staff, locale),
            facilities: sortValues(facilities, locale),
        };
    }, [categories, locale, resources, sectors, unnamedLabel]);

    const serviceValues = useMemo(() => sortValues(
        services
            .filter((service): service is Service & {id: string} => Boolean(service.id))
            .map(service => predefinedValue(
                service.id,
                getLocalizedValue(locale, service.nameLocalized) || service.name || unnamedLabel,
                service.color?.background,
            )),
        locale,
    ), [locale, services, unnamedLabel]);

    const filterOptions = useMemo(() => createSpaDailyListFilterOptions({
        t,
        locale,
        rooms,
        services: serviceValues,
        staff: spaValues.staff,
        facilities: spaValues.facilities,
    }), [locale, rooms, serviceValues, spaValues.facilities, spaValues.staff, t]);

    return <DailyListFilterButton filterOptions={filterOptions} rules={rules} onChange={onChange}/>;
}

export function RestaurantDailyListFilterButton({
    resources: restaurants,
    rules,
    onChange,
}: RestaurantDailyListFilterButtonProps) {
    const {t, i18n} = useTranslation();
    const locale = i18n.resolvedLanguage || i18n.language;
    const unnamedLabel = t("unnamed");
    const rooms = useAccommodationRoomValues(locale, unnamedLabel);
    const restaurantIds = useMemo(
        () => restaurants.map(resource => resource.id).filter((id): id is string => Boolean(id)),
        [restaurants],
    );
    const tables = useAllChildResources(restaurantIds);

    const restaurantValues = useMemo(() => sortValues(
        restaurants
            .filter((resource): resource is Resource & {id: string} => Boolean(resource.id))
            .map(resource => predefinedValue(
                resource.id,
                resourceName(resource, locale, unnamedLabel),
                resource.meta?.tagColor,
            )),
        locale,
    ), [locale, restaurants, unnamedLabel]);

    const tableValues = useMemo(() => {
        const restaurantsById = new Map(restaurants.flatMap(restaurant => (
            restaurant.id ? [[restaurant.id, restaurant] as const] : []
        )));

        return sortValues(
            tables
                .filter((table): table is Resource & {id: string} => Boolean(table.id))
                .map(table => {
                    const tableLabel = resourceName(table, locale, unnamedLabel);
                    const parent = table.parentId ? restaurantsById.get(table.parentId) : undefined;
                    const parentLabel = parent ? resourceName(parent, locale, unnamedLabel) : undefined;
                    return predefinedValue(
                        table.id,
                        parentLabel ? `${parentLabel} · ${tableLabel}` : tableLabel,
                        table.meta?.tagColor,
                    );
                }),
            locale,
        );
    }, [locale, restaurants, tables, unnamedLabel]);

    const filterOptions = useMemo(() => createRestaurantDailyListFilterOptions({
        t,
        locale,
        rooms,
        restaurants: restaurantValues,
        tables: tableValues,
    }), [locale, restaurantValues, rooms, t, tableValues]);

    return <DailyListFilterButton filterOptions={filterOptions} rules={rules} onChange={onChange}/>;
}
